import { toLocalYMD } from '../../../utils/dateUtils';
import { isClubStandLocation } from './clubWaypointBoard';

export function isStandOccupiedToday(location, today = toLocalYMD(new Date())) {
  if (!location?.sign_out_records?.length) return false;
  return location.sign_out_records.some((record) => record.date === today);
}

export function getTodaySignOutRecord(location, today = toLocalYMD(new Date())) {
  if (!location?.sign_out_records?.length) return null;
  return location.sign_out_records.find((record) => record.date === today) || null;
}

export function formatStandBoardTodayLabel(date = new Date()) {
  try {
    return date
      .toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
      .toUpperCase()
      .replace(',', '');
  } catch {
    return toLocalYMD(date);
  }
}

export function standBoardInitials(name, { isYou = false } = {}) {
  if (isYou) return 'YOU';
  const parts = String(name || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return '·';
  return parts
    .map((p) => p[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

/**
 * Map API occupancy → stand-board status.
 * Reserved is reserved for a future API; today we only expose open / on_stand.
 */
export function resolveStandBoardStatus(standRow, { currentUserId = null } = {}) {
  if (standRow?.isOccupied) {
    return {
      status: 'on_stand',
      isMine:
        currentUserId != null &&
        standRow.occupiedByUserId != null &&
        Number(standRow.occupiedByUserId) === Number(currentUserId),
    };
  }
  return { status: 'open', isMine: false };
}

/** Presentational rows for the STAND BOARD list under the club map. */
export function buildStandBoardRows(stands, { currentUserId = null } = {}) {
  if (!Array.isArray(stands)) return [];

  return stands.map((stand) => {
    const { status, isMine } = resolveStandBoardStatus(stand, { currentUserId });
    const period = stand.timePeriod ? String(stand.timePeriod).toUpperCase() : null;
    let chip = 'OPEN';
    let meta = 'Available today';
    let badge = '·';

    if (status === 'on_stand') {
      badge = standBoardInitials(isMine ? 'You' : stand.occupiedBy, { isYou: isMine });
      chip = isMine ? (period ? `YOU · ${period}` : 'YOU · ON STAND') : 'ON STAND';
      meta = isMine
        ? (period ? `You · ${period} sit` : 'You are on stand')
        : [stand.occupiedBy, period ? `${period} sit` : null].filter(Boolean).join(' · ') || 'Occupied';
    }

    return {
      ...stand,
      status,
      isMine,
      badge,
      chip,
      meta,
    };
  });
}

export function countOpenStands(rows) {
  if (!Array.isArray(rows)) return 0;
  return rows.filter((r) => r.status === 'open').length;
}

/** Alphabetical stand rows for the mobile tag-out picker. */
export function buildSortedClubStands(huntLocations, today = toLocalYMD(new Date())) {
  if (!Array.isArray(huntLocations)) return [];

  return huntLocations
    .filter((loc) => isClubStandLocation(loc))
    .map((loc) => {
      const isOccupied = isStandOccupiedToday(loc, today);
      const todayRecord = isOccupied ? getTodaySignOutRecord(loc, today) : null;
      return {
        id: loc.id,
        name: loc.name || `Stand ${loc.id}`,
        isOccupied,
        occupiedBy: todayRecord?.user_name || null,
        occupiedByUserId: todayRecord?.user_id ?? null,
        timePeriod: todayRecord?.time_period || null,
        notes: loc.notes || '',
        geojson: loc.geojson,
        location: loc
      };
    })
    .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
}

export function filterClubStands(stands, query) {
  const q = (query || '').trim().toLowerCase();
  if (!q) return stands;
  return stands.filter((stand) => stand.name.toLowerCase().includes(q));
}

export function buildClubStandFeature(standRow) {
  const loc = standRow.location || standRow;
  const isOccupied = standRow.isOccupied ?? isStandOccupiedToday(loc);
  const optimalWinds = Array.isArray(loc.optimal_winds)
    ? loc.optimal_winds.filter(Boolean)
    : [];
  const optimalWind = loc.optimal_wind || '';
  const winds = optimalWinds.length
    ? optimalWinds
    : (optimalWind ? [optimalWind] : []);
  return {
    id: `location-${loc.id}`,
    name: loc.name,
    notes: loc.notes || '',
    featureType: 'hunt_location',
    geojson: loc.geojson,
    isOccupied,
    optimal_wind: optimalWind,
    optimal_winds: winds,
    location: loc,
    properties: {
      id: `location-${loc.id}`,
      name: loc.name,
      featureType: 'hunt_location',
      isHuntLocation: true,
      isOccupied,
      notes: loc.notes || '',
      optimal_wind: optimalWind,
      optimal_winds: winds,
    }
  };
}
